<template>
  <v-dialog persistent v-model="dialog" max-width="500px">
    <v-stepper v-model="step">
      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card>
            <v-card-title>
              <span class="headline">{{
                trialItem
                  ? $t("Assign a standard licence")
                  : $t("Renew licence")
              }}</span>
            </v-card-title>
            <v-card-subtitle>{{ $t("* Required fields") }}</v-card-subtitle>
            <v-card-text>
              <validation-observer ref="form">
                <v-container>
                  <validation-provider
                    v-slot="{ errors }"
                    name="plan"
                    rules="required_price_plan"
                  >
                    <v-select
                      :label="`${$t('Pricing plan')} *`"
                      :items="prices"
                      v-model="price"
                      :error-messages="errors"
                      return-object
                      :item-text="(item) => `${item.type}-${item.plan}`"
                    >
                      <template v-slot:item="{ item }">
                        {{ formatPlan(item) }}
                        <v-spacer></v-spacer>
                        {{ toPrice(item.amount).toFormat() }}
                      </template>
                      <template v-slot:selection="{ item }">
                        {{ formatPlan(item) }}
                        <v-spacer></v-spacer>
                        {{ toPrice(item.amount).toFormat() }}
                      </template>
                    </v-select>
                  </validation-provider>
                </v-container>
                <total-licence-price
                  :price="selectedPrice"
                  :options="options"
                  :discount="null"
                  :nextDiscount="null"
                ></total-licence-price>
              </validation-observer>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click.stop="dialog = false">{{
                $t("Cancel")
              }}</v-btn>
              <v-btn
                v-show="price.plan !== ''"
                data-cy="button-next-licence"
                color="primary"
                :loading="loading || licenceToBuy === null"
                text
                @click.stop="licenceToBuy ? (step = 2) : save()"
                >{{ licenceToBuy ? $t("Next") : $t("Save") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-stepper-content>
        <v-stepper-content step="2">
          <v-card>
            <v-card-title>
              <span class="headline">{{
                $t("Purchase and billing conditions")
              }}</span>
            </v-card-title>
            <v-card-text>
              {{ $tc("terms-of-sale", 1, { total }) }}
              <v-checkbox
                data-cy="checkbox-accept"
                :label="$t('Accept')"
                v-model="termsAccepted"
              ></v-checkbox>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click.stop="step = 1">{{
                $t("Back")
              }}</v-btn>
              <v-btn
                data-cy="button-buy-licence"
                color="primary"
                :loading="loading"
                text
                @click.stop="save"
                :disabled="!termsAccepted"
                >{{ $t("Save") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-dialog>
</template>

<script>
import Dinero from "dinero.js";
import snackbar from "@/mixins/snackbar";
import priceMixin from "@/mixins/priceMixin";
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import TotalLicencePrice from "./TotalLicencePrice.vue";

export default {
  name: "RenewLicenceDialog",
  props: ["value"],
  components: { ValidationObserver, ValidationProvider, TotalLicencePrice },
  mixins: [snackbar, priceMixin],
  data() {
    return {
      loading: false,
      step: 1,
      termsAccepted: false,
    };
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.$store.dispatch("licences/resetRenewedItem");
        this.loading = false;
        this.step = 1;
        this.termsAccepted = false;
        this.$refs.form.reset();
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    ...mapState({
      selectedItem: (state) => state.licences.selectedItem,
      trialItem: (state) => state.licences.selectedItem?.trial,
      application: (state) =>
        state.applications.items.find(
          (x) => x.id === state.licences.selectedItem?.application_id
        ),
      total: (state) => state.licences.total,
    }),
    ...mapFields("licences", {
      price: "renewedItem.price",
    }),
    prices() {
      return this.application?.prices.filter((p) => p.option === false) ?? [];
    },
    selectedPrice() {
      let price = this.prices.find(
        (x) => x.plan === this.price.plan && x.type == this.price.type
      );

      return price;
    },
    options() {
      return (
        this.application?.prices.filter(
          (p) =>
            p.option === true &&
            this.selectedItem.options.findIndex((o) => o.type === p.type) !== -1
        ) ?? []
      );
    },
    licenceToBuy() {
      if (this.plan !== "") {
        return (
          this.$store.getters["licences/licencesAvailableForRenew"](
            this.price
          ) === 0
        );
      } else {
        return null;
      }
    },
  },
  methods: {
    toPrice(value) {
      return Dinero({ amount: value });
    },
    save() {
      this.$refs.form.validate().then((success) => {
        if (!success) {
          return;
        }
        this.loading = true;
        this.$store
          .dispatch("licences/renewLicence")
          .then(() => {
            this.loading = false;
            this.dialog = false;
            this.snackbar(
              "success",
              this.$t("snackbar-message-licence.renewed")
            );
          })
          .catch(this.responseError);
      });
    },
    responseError(error) {
      this.loading = false;
      if (error.response && error.response.status === 422) {
        let errors = error.response.data.errors;
        this.$refs.form.setErrors(errors);
      } else {
        this.snackbar("error", this.$t("snackbar-message-licence.error"));
      }
    },
  },
};
</script>
